import React, { useMemo } from 'react';

import { DataLoadingStatus, LogoProps } from 'common/components/RewindModal';
import { RewindProvider } from 'common/components/RewindModal/RewindContext';
import FlashbackType from 'common/components/RewindModal/screens/FlashbackType';
import Loading from 'common/components/RewindModal/screens/Loading';
import ScreenAverageRating from 'common/components/RewindModal/screens/ScreenAverageRating';
import ScreenConclusion from 'common/components/RewindModal/screens/ScreenConclusion';
import ScreenConclusionGlobal from 'common/components/RewindModal/screens/ScreenConclusionGlobal';
import ScreenFavoriteCelebrity from 'common/components/RewindModal/screens/ScreenFavoriteCelebrity';
import ScreenRepartition from 'common/components/RewindModal/screens/ScreenRepartition';
import ScreenTop10Content from 'common/components/RewindModal/screens/ScreenTop10';
import ScreenTopContent from 'common/components/RewindModal/screens/ScreenTopContent';
import ScreenTopGlobalMovies from 'common/components/RewindModal/screens/ScreenTopGlobalMovies';
import ScreenTopGlobalSeries from 'common/components/RewindModal/screens/ScreenTopGlobalSeries';
import ScreenViewingTime from 'common/components/RewindModal/screens/ScreenViewingTime';
import Top3MoviesIntro from 'common/components/RewindModal/screens/Top3MoviesIntro';
import Top3SeriesIntro from 'common/components/RewindModal/screens/Top3SeriesIntro';
import TopGlobalMoviesIntro from 'common/components/RewindModal/screens/TopGlobalMoviesIntro';
import TopGlobalSeriesIntro from 'common/components/RewindModal/screens/TopGlobalSeriesIntro';
import { CloseModal } from 'common/module/ui/lazy-modal/types';
import { RewindConfig, RewindImages } from 'common/opes/loadOpes';

import RewindCaptif from './screens/RewindCaptif';
import ScreenBilanUser from './screens/ScreenBilanUser';
import ScreenComparison from './screens/ScreenComparison';
import ScreenPodium from './screens/ScreenPodium';
import ScreenUserRanking from './screens/ScreenUserRanking';
import TopContentIntro from './screens/TopContentIntro';

type RewindModalComponentProps = {
  closeModal: CloseModal;
  onNext: () => void;
  onStep: (currentStep: number) => void;
  screenName: ScreenNames;
  isLastStep: boolean;
  allScreens: any[];
  userNickname?: string;
  dataLoadingStatus?: DataLoadingStatus;
  step: number;
  steps: number;
  ope?: RewindConfig;
  images?: RewindImages;
  headerLogo?: LogoProps;
  flashbackType?: number;
  trackingScreenName?: string;
};

const RewindModalComponent = ({
  onNext,
  onStep,
  screenName,
  isLastStep,
  allScreens,
  userNickname,
  dataLoadingStatus,
  step,
  steps,
  ope,
  images,
  headerLogo,
  flashbackType,
  trackingScreenName
}: RewindModalComponentProps) => {
  const currentScreen = useMemo(() => {
    if (screenName.startsWith('top10_')) {
      const originalScreenName = `screen_${screenName
        .replace('_intro', '')
        .replace('_reveal', '')}`;
      return allScreens.find(s => s.name === originalScreenName);
    }
    // For specific intros of global screens
    if (screenName === 'screen_top_global_movies_2025_intro') {
      return allScreens.find(s => s.name === 'screen_top_global_movies_2025');
    }
    if (screenName === 'screen_top_global_series_2025_intro') {
      return allScreens.find(s => s.name === 'screen_top_global_series_2025');
    }
    if (screenName === 'top_content_intro') {
      // For classic top_content_intro, find the right screen based on context
      return allScreens.find(s => s.name.startsWith('screen_top_'));
    }
    if (screenName.startsWith('top_content_')) {
      return allScreens.find(s => s.name.startsWith('screen_top_'));
    }
    // For type 6 global screens with reveals
    if (
      screenName.startsWith('screen_top_global_movies_2025_reveal_') ||
      screenName.startsWith('screen_top_global_series_2025_reveal_')
    ) {
      // Extract base name (without _reveal_X)
      const baseScreenName = screenName.replace(/_reveal_\d+$/, '');
      return allScreens.find(s => s.name === baseScreenName);
    }
    // For top3 intros
    if (screenName === 'screen_movie_top3_intro') {
      return allScreens.find(s => s.name === 'screen_movie_top3');
    }
    if (screenName === 'screen_series_top3_intro') {
      return allScreens.find(s => s.name === 'screen_series_top3');
    }
    // For type 6 global screens without reveal (fallback)
    if (
      screenName === 'screen_top_global_movies_2025' ||
      screenName === 'screen_top_global_series_2025'
    ) {
      return allScreens.find(s => s.name === screenName);
    }
    return allScreens.find(s => s.name === screenName);
  }, [screenName, allScreens]);

  const data = currentScreen?.data;
  const entities = currentScreen?.entities;

  // Get flashbackType to avoid displaying ScreenTopContent for type 6
  const flashbackTypeFromScreens = useMemo(() => {
    const flashbackScreen = allScreens.find(s => s.name === 'flashback_type');
    return flashbackScreen?.data?.flashback_type;
  }, [allScreens]);

  const renderScreen = () => {
    // Specific intros for type 6 global screens
    if (screenName === 'screen_top_global_movies_2025_intro') {
      return <TopGlobalMoviesIntro onNext={onNext} isLastStep={isLastStep} />;
    }

    if (screenName === 'screen_top_global_series_2025_intro') {
      return <TopGlobalSeriesIntro onNext={onNext} isLastStep={isLastStep} />;
    }

    // Intros for top3 (except for type 6)
    if (screenName === 'screen_movie_top3_intro') {
      if (flashbackTypeFromScreens === 6) {
        onNext();
        return null;
      }
      return (
        <Top3MoviesIntro
          onNext={onNext}
          isLastStep={isLastStep}
          flashbackType={flashbackTypeFromScreens}
        />
      );
    }

    if (screenName === 'screen_series_top3_intro') {
      if (flashbackTypeFromScreens === 6) {
        onNext();
        return null;
      }
      return (
        <Top3SeriesIntro
          onNext={onNext}
          isLastStep={isLastStep}
          flashbackType={flashbackTypeFromScreens}
        />
      );
    }

    if (
      screenName.includes('top_content_intro') ||
      (screenName.startsWith('top10_') && screenName.endsWith('_intro'))
    ) {
      return (
        <TopContentIntro
          entities={entities}
          allScreens={allScreens}
          onNext={onNext}
          isLastStep={isLastStep}
          trackingScreenName={trackingScreenName}
        />
      );
    }

    // Handling of type 6 global screens with reveals - Movies
    if (screenName.startsWith('screen_top_global_movies_2025_reveal_')) {
      const isReveal3 = screenName === 'screen_top_global_movies_2025_reveal_3';
      const shareText = isReveal3
        ? 'Partager le Top Films Allociné'
        : 'Partager mon top films';
      return (
        <ScreenTopGlobalMovies
          entities={entities}
          onNext={onNext}
          isLastStep={isLastStep}
          screenName={screenName}
          shareText={shareText}
        />
      );
    }

    // Handling of type 6 global screens with reveals - Series
    if (screenName.startsWith('screen_top_global_series_2025_reveal_')) {
      const isReveal3 = screenName === 'screen_top_global_series_2025_reveal_3';
      const shareText = isReveal3
        ? 'Partager le Top Séries Allociné'
        : 'Partager mon top séries';
      return (
        <ScreenTopGlobalSeries
          entities={entities}
          onNext={onNext}
          isLastStep={isLastStep}
          screenName={screenName}
          shareText={shareText}
        />
      );
    }

    // ScreenTopContent should not be displayed for type 6
    // (type 6 uses ScreenTopGlobalMovies and ScreenTopGlobalSeries)
    if (
      screenName.startsWith('top_content_reveal_') &&
      flashbackTypeFromScreens !== 6
    ) {
      const isSeries = entities?.[0]?.identifier.includes('series');
      const shareText = isSeries
        ? 'Partager mon top séries'
        : 'Partager mon top films';
      return (
        <ScreenTopContent
          entities={entities}
          onNext={onNext}
          isLastStep={isLastStep}
          screenName={screenName}
          shareText={shareText}
        />
      );
    }

    if (screenName.startsWith('top10_') && screenName.endsWith('_reveal')) {
      return (
        <ScreenTop10Content
          data={data}
          entities={entities}
          onNext={onNext}
          isLastStep={isLastStep}
          screenName={screenName}
          allScreens={allScreens}
          trackingScreenName={trackingScreenName}
        />
      );
    }

    if (screenName.startsWith('favorite_celebrity_')) {
      return (
        <ScreenFavoriteCelebrity
          screenName={screenName}
          allScreens={allScreens}
          onNext={onNext}
          isLastStep={isLastStep}
          onStep={onStep}
          step={step}
          steps={steps}
          trackingScreenName={trackingScreenName}
        />
      );
    }

    if (screenName.includes('_comparison')) {
      return (
        <ScreenComparison
          data={data}
          entities={entities}
          onNext={onNext}
          isLastStep={isLastStep}
          trackingScreenName={trackingScreenName}
        />
      );
    }

    if (
      screenName.startsWith('screen_top_theater_') ||
      screenName.startsWith('screen_top_series_online') ||
      screenName.endsWith('_top3')
    ) {
      return (
        <ScreenPodium
          data={data}
          entities={entities}
          onNext={onNext}
          isLastStep={isLastStep}
          screenName={screenName}
          allScreens={allScreens}
          step={step}
          steps={steps}
          onStep={onStep}
          trackingScreenName={trackingScreenName}
        />
      );
    }

    switch (screenName) {
      case 'screen_upload_data':
        return (
          <Loading
            dataLoadingStatus={dataLoadingStatus}
            onNext={onNext}
            steps={steps}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'flashback_type':
        return (
          <FlashbackType
            data={data}
            onNext={onNext}
            isLastStep={isLastStep}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'screen_average_rating':
        return (
          <ScreenAverageRating
            data={data}
            entities={entities}
            allScreens={allScreens}
            onNext={onNext}
            isLastStep={isLastStep}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'screen_viewing_time':
        return (
          <ScreenViewingTime
            data={data}
            onNext={onNext}
            isLastStep={isLastStep}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'screen_repartition':
        return (
          <ScreenRepartition
            data={data}
            userNickname={userNickname}
            allScreens={allScreens}
            onNext={onNext}
            isLastStep={isLastStep}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'screen_user_ranking':
        return (
          <ScreenUserRanking
            data={data}
            onNext={onNext}
            isLastStep={isLastStep}
            step={step}
            steps={steps}
            onStep={onStep}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'screen_conclusion_global':
        return (
          <ScreenConclusionGlobal onNext={onNext} isLastStep={isLastStep} />
        );
      case 'screen_conclusion':
        return (
          <ScreenConclusion
            onNext={onNext}
            isLastStep={isLastStep}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'screen_bilan_user':
        return (
          <ScreenBilanUser
            data={data}
            onNext={onNext}
            isLastStep={isLastStep}
            userNickname={userNickname}
            trackingScreenName={trackingScreenName}
          />
        );
      case 'rewind_captif':
        return (
          <RewindCaptif
            ope={ope}
            images={images}
            headerLogo={headerLogo}
            trackingScreenName={trackingScreenName}
          />
        );
      default:
        return <h2>Écran &quot;{screenName}&quot; non trouvé.</h2>;
    }
  };

  return (
    <RewindProvider
      flashbackType={flashbackType}
      screenName={screenName}
      trackingScreenName={trackingScreenName}
    >
      {renderScreen()}
    </RewindProvider>
  );
};

export default RewindModalComponent;
